import React, {
  useState, useEffect, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Modal from '../../../../../shared/_TBComponents/Modal';
import SelectBox from '../../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../../shared/_TBComponents/Text';
import { SquareCheckbox } from '../tables/NavigatorManageTable';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const ColumnGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
  margin-top: 8px;
  margin-bottom: 24px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #333333;
  width: calc(33.333% - 16px);

  @media (max-width: 580px) {
    width: calc(50% - 12px);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderDivider = styled.div`
  border-top: 1px solid #D6D6D6;
  margin: 16px -24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const FILTER_MODE_OPTIONS = [
  { id: 'any', name: 'Match ANY of the following filters' },
  { id: 'all', name: 'Match ALL of the following filters' },
];

const GENDER_OPTIONS = [
  { id: 1, name: 'Male' },
  { id: 2, name: 'Female' },
  { id: 3, name: 'Undefined' },
];

const BASE_COLUMN_OPTIONS = [
  { key: 'userId', label: 'Show User ID' },
  { key: 'externalId', label: 'Show External ID' },
  { key: 'ssoId', label: 'Show SSO ID' },
  { key: 'smartabaseId', label: 'Show Smartabase ID' },
  { key: 'joinedDate', label: 'Show Joined Date' },
];

const FilterAthletesModal = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  groups,
  calendars,
  activeFilters,
  columnVisibility,
}) => {
  const [filterMode, setFilterMode] = useState('any');
  const [filterGroups, setFilterGroups] = useState([]);
  const [filterCalendars, setFilterCalendars] = useState([]);
  const [filterGenders, setFilterGenders] = useState([]);
  const [localColumnVisibility, setLocalColumnVisibility] = useState(columnVisibility);

  const orgSettings = useSelector((state) => state.auth.data.organizationSettings);
  const smartabaseEnabled = orgSettings?.smartabaseEnabled;
  const ssoEnabled = orgSettings?.sso?.enabled;
  const ssoLoginName = orgSettings?.sso?.loginName;

  const columnOptions = useMemo(
    () => BASE_COLUMN_OPTIONS
      .filter(({ key }) => {
        if (key === 'ssoId') return ssoEnabled;
        if (key === 'smartabaseId') return smartabaseEnabled;
        return true;
      })
      .map((option) => (
        option.key === 'ssoId'
          ? { ...option, label: `Show ${ssoLoginName || 'SSO'} ID` }
          : option
      )),
    [ssoEnabled, smartabaseEnabled, ssoLoginName],
  );

  useEffect(() => {
    if (isOpen) {
      setFilterMode(activeFilters.filterMode || 'any');
      setFilterGroups(activeFilters.filterGroups || []);
      setFilterCalendars(activeFilters.filterCalendars || []);
      setFilterGenders(activeFilters.filterGenders || []);
      setLocalColumnVisibility(columnVisibility);
    }
  }, [isOpen]);

  const handleApply = () => {
    onApply({
      filterMode,
      filterGroups,
      filterCalendars,
      filterGenders,
      columnVisibility: localColumnVisibility,
    });
  };

  const toggleColumn = (key) => {
    setLocalColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const hasSelections = (
    filterGroups.length > 0
    || filterCalendars.length > 0
    || filterGenders.length > 0
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      customBorderRadius='8px'
      customPadding='24px'
      customMaxWidth='700px'
      customMinWidth='min(600px, 100vw)'
    >
      <ContentWrapper>
        <ModalHeader>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, color: '#1A1A1A' }}>
            Filter Athletes
          </Text>
          <Button
            icon='remove'
            iconSize={15}
            onClick={onClose}
            fontSize='13px'
            txtColor='#9E9E9E'
            noBorder
            iconOnly
            styledWrapperProps={{ padding: '0' }}
          />
        </ModalHeader>
        <HeaderDivider />
        <Section>
          <SelectBox
            title='Filter Condition'
            options={FILTER_MODE_OPTIONS}
            value={FILTER_MODE_OPTIONS.find((o) => o.id === filterMode)}
            onChangeFunction={(opt) => setFilterMode(opt.id)}
            isClearable={false}
            isSearchable={false}
            styledProps={{ border: '1px solid #EEEEEE' }}
            titleStyledProps={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}
            height='auto'
          />
        </Section>
        <Section>
          <SelectBox
            title='Groups'
            options={groups}
            value={filterGroups}
            onChangeFunction={(opts) => setFilterGroups(opts || [])}
            isMulti
            placeholder='Select group(s) to filter'
            styledProps={{ border: '1px solid #EEEEEE' }}
            titleStyledProps={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}
            height='auto'
          />
        </Section>
        <Section>
          <SelectBox
            title='Calendars'
            options={calendars}
            value={filterCalendars}
            onChangeFunction={(opts) => setFilterCalendars(opts || [])}
            isMulti
            placeholder='Select calendar(s) to filter'
            styledProps={{ border: '1px solid #EEEEEE' }}
            titleStyledProps={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}
            height='auto'
          />
        </Section>
        <Section>
          <SelectBox
            title='Gender'
            options={GENDER_OPTIONS}
            value={filterGenders}
            onChangeFunction={(opts) => setFilterGenders(opts || [])}
            isMulti
            placeholder='Select gender(s) to filter'
            styledProps={{ border: '1px solid #EEEEEE' }}
            titleStyledProps={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}
            height='auto'
          />
        </Section>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}>
          Column Visibility
        </Text>
        <ColumnGrid>
          {columnOptions.map(({ key, label }) => (
            <CheckboxLabel key={key}>
              <SquareCheckbox
                checked={!!localColumnVisibility[key]}
                onChange={() => toggleColumn(key)}
              />
              {label}
            </CheckboxLabel>
          ))}
        </ColumnGrid>
        <Footer>
          {hasSelections && (
            <Button
              name='Reset'
              fontSize='14px'
              txtColor='#FF0505'
              bgColor='#FFF'
              borderColor='#FF0505'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={onReset}
            />
          )}
          <FooterActions>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={onClose}
            />
            <Button
              name='Apply'
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={handleApply}
            />
          </FooterActions>
        </Footer>
      </ContentWrapper>
    </Modal>
  );
};

FilterAthletesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ).isRequired,
  calendars: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ).isRequired,
  activeFilters: PropTypes.shape({
    filterMode: PropTypes.string,
    filterGroups: PropTypes.arrayOf(PropTypes.shape({})),
    filterCalendars: PropTypes.arrayOf(PropTypes.shape({})),
    filterGenders: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  columnVisibility: PropTypes.shape({
    userId: PropTypes.bool,
    externalId: PropTypes.bool,
    ssoId: PropTypes.bool,
    smartabaseId: PropTypes.bool,
    joinedDate: PropTypes.bool,
  }).isRequired,
};

export default FilterAthletesModal;
